import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  Shield, Ticket, TrendingUp, BookOpen, Cloud,
  ArrowRight, X, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';

const TOUR_STEPS = [
  {
    title: 'Welcome to TechOps',
    description: 'Your unified platform for IT operations, cybersecurity, and business growth. Let us show you around.',
    icon: Shield,
    color: 'bg-[#D4A843]',
    route: null,
  },
  {
    title: 'Ticket Management',
    description: 'Create, assign, and track IT support tickets with SLA timers, AI triage, and technician dispatch.',
    icon: Ticket,
    color: 'bg-amber-500',
    route: '/dashboard?tab=Dashboard',
  },
  {
    title: 'Cyber Operations',
    description: 'Monitor security logs, manage detection rules, investigate alerts, and hunt threats with AI assistance.',
    icon: Shield,
    color: 'bg-red-600',
    route: '/cyber',
  },
  {
    title: 'Growth Hub',
    description: 'Run business audits, generate websites, manage referral partners, and grow your digital presence.',
    icon: TrendingUp,
    color: 'bg-emerald-600',
    route: '/growth',
  },
  {
    title: 'Knowledge Base',
    description: 'Find solutions, create articles, and use AI to generate documentation for your team.',
    icon: BookOpen,
    color: 'bg-purple-600',
    route: '/kb',
  },
  {
    title: 'Cloud Migration',
    description: 'Manage migration projects with AI-powered assessments and step-by-step tracking.',
    icon: Cloud,
    color: 'bg-sky-600',
    route: '/migration',
  },
];

export default function ProductTour() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/user/tour-status');
        if (!data.tour_completed) setVisible(true);
      } catch (e) { /* handled */ }
      setChecked(true);
    })();
  }, []);

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    setVisible(false);
    try {
      await api.put('/api/user/tour-status', { completed: true });
    } catch (e) { /* handled */ }
  };

  const handleSkip = async () => {
    setVisible(false);
    try {
      await api.put('/api/user/tour-status', { completed: true });
    } catch (e) { /* handled */ }
  };

  const handleGoTo = () => {
    const route = TOUR_STEPS[step].route;
    if (route) navigate(route);
    handleNext();
  };

  if (!checked || !visible) return null;

  const current = TOUR_STEPS[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="product-tour">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />

      {/* Tour Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fade-in" data-testid="tour-card">
        {/* Header */}
        <div className={`${current.color} p-8 text-center relative`}>
          <button onClick={handleSkip} className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="tour-skip">
            <X className="w-5 h-5" />
          </button>
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">{current.title}</h2>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-sm text-gray-600 text-center leading-relaxed">{current.description}</p>

          {/* Progress Dots */}
          <div className="flex items-center justify-center gap-2 mt-6 mb-6">
            {TOUR_STEPS.map((_, i) => (
              <div key={`item-${i}`} className={`w-2 h-2 rounded-full transition-all ${i === step ? 'bg-[#D4A843] w-6' : i < step ? 'bg-[#D4A843]/40' : 'bg-gray-200'}`} data-testid={`tour-dot-${i}`} />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button onClick={handleSkip} className="text-xs text-gray-400 hover:text-gray-600" data-testid="tour-skip-text">
              Skip tour
            </button>
            <div className="flex gap-2">
              {current.route && (
                <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={handleGoTo} data-testid="tour-goto">
                  Explore <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              )}
              <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs px-6" onClick={handleNext} data-testid="tour-next">
                {step === TOUR_STEPS.length - 1 ? 'Get Started' : 'Next'} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
